const KMZ_FILE_SUFFIX_POINT_NAME_TOKEN = '<pointName>'
const KMZ_DEFAULT_ACTION_GROUP_MODE = 'sequence'
const KMZ_DEFAULT_ACTION_TRIGGER_TYPE = 'reachPoint'
const KMZ_ACTION_TRIGGER_TYPES = ['reachPoint', 'betweenAdjacentPoints', 'multipleTiming']

const normalizeKmzActionEntry = function (entry) {
  if (!entry || typeof entry !== 'object') return null
  const actuatorRaw = (entry.actuator !== undefined && entry.actuator !== null)
    ? entry.actuator
    : (entry['wpml:actionActuatorFunc'] !== undefined ? entry['wpml:actionActuatorFunc'] : '')
  const actuator = String(actuatorRaw || '').trim()
  if (!actuator) return null
  const paramsSrc = (entry.params && typeof entry.params === 'object')
    ? entry.params
    : ((entry['wpml:actionActuatorFuncParam'] && typeof entry['wpml:actionActuatorFuncParam'] === 'object')
      ? entry['wpml:actionActuatorFuncParam']
      : {})
  const params = {}
  Object.keys(paramsSrc || {}).forEach((key) => {
    if (key === '_text' || key.startsWith('@')) return
    const shortKey = String(key || '').replace(/^[a-zA-Z_][\w-]*:/, '')
    let rawVal = paramsSrc[key]
    if (rawVal && typeof rawVal === 'object' && rawVal._text !== undefined) rawVal = rawVal._text
    if (rawVal === undefined || rawVal === null) return
    params[shortKey] = String(rawVal)
  })
  return { actuator: actuator, params: params }
}

const mapKmzActionFileSuffixValues = function (actions, resolver) {
  const normalized = (Array.isArray(actions) ? actions : [])
    .map(normalizeKmzActionEntry)
    .filter((item) => !!item)
  return normalized.map((item) => {
    if (!item || !item.params || typeof item.params !== 'object') return item
    if (!Object.prototype.hasOwnProperty.call(item.params, 'fileSuffix')) return item
    const current = String(item.params.fileSuffix === undefined ? '' : item.params.fileSuffix)
    const nextRaw = (typeof resolver === 'function') ? resolver(current, item) : current
    const next = String(nextRaw === undefined || nextRaw === null ? '' : nextRaw)
    if (next === current) return item
    return Object.assign({}, item, {
      params: Object.assign({}, item.params, { fileSuffix: next })
    })
  })
}

const applyKmzActionFileSuffixDisplayMode = function (actions, mode, pointNameResolver) {
  const normalizedMode = String(mode || 'raw').trim().toLowerCase()
  if (normalizedMode === 'token') {
    return mapKmzActionFileSuffixValues(actions, function () {
      return KMZ_FILE_SUFFIX_POINT_NAME_TOKEN
    })
  }
  if (normalizedMode === 'pointname') {
    const pointName = (typeof pointNameResolver === 'function')
      ? String(pointNameResolver() || '')
      : String(pointNameResolver || '')
    return mapKmzActionFileSuffixValues(actions, function () {
      return pointName
    })
  }
  return (Array.isArray(actions) ? actions : [])
    .map(normalizeKmzActionEntry)
    .filter((item) => !!item)
}

const resolveKmzActionFileSuffixPointNameTokens = function (actions, pointNameResolver) {
  const pointName = (typeof pointNameResolver === 'function')
    ? String(pointNameResolver() || '')
    : String(pointNameResolver || '')
  return mapKmzActionFileSuffixValues(actions, function (current) {
    return String(current || '').trim() === KMZ_FILE_SUFFIX_POINT_NAME_TOKEN ? pointName : current
  })
}

const normalizeKmzIntegerLikeText = function (value, fallbackValue) {
  const fallback = Number.isFinite(Number(fallbackValue)) ? String(Math.trunc(Number(fallbackValue))) : '0'
  const text = String(value === null || value === undefined ? '' : value).trim()
  if (!text) return fallback
  const n = Number(text)
  if (!Number.isFinite(n)) return fallback
  return String(Math.trunc(n))
}

const normalizeKmzActionTriggerEntry = function (triggerEntry) {
  const src = (triggerEntry && typeof triggerEntry === 'object') ? triggerEntry : {}
  const out = {}
  Object.keys(src || {}).forEach((key) => {
    if (key === '_text' || key.startsWith('@')) return
    const shortKey = String(key || '').replace(/^[a-zA-Z_][\w-]*:/, '')
    let rawVal = src[key]
    if (rawVal && typeof rawVal === 'object' && rawVal._text !== undefined) rawVal = rawVal._text
    if (rawVal === undefined || rawVal === null) return
    out[shortKey] = String(rawVal)
  })
  if (!Object.prototype.hasOwnProperty.call(out, 'actionTriggerType') || !String(out.actionTriggerType || '').trim()) {
    out.actionTriggerType = KMZ_DEFAULT_ACTION_TRIGGER_TYPE
  }
  return out
}

const normalizeKmzActionGroupEntry = function (entry, fallbackPointIndex) {
  if (!entry || typeof entry !== 'object') return null
  const fallbackIndex = Number.isFinite(Number(fallbackPointIndex)) ? Number(fallbackPointIndex) : 0
  const idRaw = entry.actionGroupId !== undefined ? entry.actionGroupId : entry['wpml:actionGroupId']
  const startRaw = entry.actionGroupStartIndex !== undefined ? entry.actionGroupStartIndex : entry['wpml:actionGroupStartIndex']
  const endRaw = entry.actionGroupEndIndex !== undefined ? entry.actionGroupEndIndex : entry['wpml:actionGroupEndIndex']
  const modeRaw = entry.actionGroupMode !== undefined ? entry.actionGroupMode : entry['wpml:actionGroupMode']
  const triggerRaw = entry.actionTrigger !== undefined ? entry.actionTrigger : entry['wpml:actionTrigger']
  const actionsRaw = entry.actions !== undefined ? entry.actions : entry['wpml:action']

  let actions = []
  if (Array.isArray(actionsRaw)) {
    actions = actionsRaw
  } else if (actionsRaw && typeof actionsRaw === 'object') {
    const numericKeys = Object.keys(actionsRaw).filter((k) => /^\d+$/.test(String(k)))
    actions = numericKeys.length
      ? numericKeys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).map((k) => actionsRaw[k])
      : [actionsRaw]
  }
  const normalizedActions = actions
    .map(normalizeKmzActionEntry)
    .filter((item) => !!item)

  const mode = String(modeRaw === undefined || modeRaw === null ? KMZ_DEFAULT_ACTION_GROUP_MODE : modeRaw).trim() || KMZ_DEFAULT_ACTION_GROUP_MODE
  return {
    actionGroupId: normalizeKmzIntegerLikeText(idRaw, fallbackIndex),
    actionGroupStartIndex: normalizeKmzIntegerLikeText(startRaw, fallbackIndex),
    actionGroupEndIndex: normalizeKmzIntegerLikeText(endRaw, fallbackIndex),
    actionGroupMode: mode,
    actionTrigger: normalizeKmzActionTriggerEntry(triggerRaw),
    actions: normalizedActions
  }
}

export {
  KMZ_FILE_SUFFIX_POINT_NAME_TOKEN,
  KMZ_DEFAULT_ACTION_GROUP_MODE,
  KMZ_DEFAULT_ACTION_TRIGGER_TYPE,
  KMZ_ACTION_TRIGGER_TYPES,
  normalizeKmzActionEntry,
  mapKmzActionFileSuffixValues,
  applyKmzActionFileSuffixDisplayMode,
  resolveKmzActionFileSuffixPointNameTokens,
  normalizeKmzIntegerLikeText,
  normalizeKmzActionTriggerEntry,
  normalizeKmzActionGroupEntry
}
